import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Opportunity from "@/services/opportunity/opportunity";
import OpportunityContact from "@/services/opportunity/opportunity-contact";

export interface OpportunityByContactParams {
  contactId: string;
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

// Opportunity has no contactId column — the link only exists via the OpportunityContact
// join table (see ContactOverview.vue's "existingOpportunity" lookup), so this can't
// filter Opportunity by contactId directly. It looks up the join record first to get
// the opportunityId, then fetches the full opportunity (same { id, organizationId }
// shape useOpportunity.ts already uses) to get its name/stage for display.
export function useOpportunityByContact(
  params: Ref<OpportunityByContactParams | null> | OpportunityByContactParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["opportunity", "byContact", "disabled"];
    return ["opportunity", "byContact", p.contactId, p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.contactId && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const linkResult = await OpportunityContact.getOpportunityContacts({
        contactId: p.contactId,
        organizationId: p.organizationId,
      });

      if (!linkResult.data.status) {
        throw new Error(linkResult.data.msg || "Failed to load opportunity link");
      }

      const links = linkResult.data.data;
      const opportunityId = links && links.length > 0 ? links[0].opportunityId : null;
      if (!opportunityId) return null;

      const result = await Opportunity.getOpportunities({
        id: opportunityId,
        organizationId: p.organizationId,
      });

      if (result.data.status) {
        const opportunities = result.data.data;
        return opportunities && opportunities.length > 0 ? opportunities[0] : null;
      }

      throw new Error(result.data.msg || "Failed to load opportunity");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
